import 'core-js/stable';
import 'regenerator-runtime/runtime';

import type {
  PenumbraAPI,
  PenumbraErrorEmit,
  PenumbraFile,
  PenumbraZipWriter,
  ProgressEmit,
  RemoteResource,
} from '@transcend-io/penumbra';

import { logger } from '@main/core-ui';

import { SHOULD_MOCK_PENUMBRA } from './args';

export type {
  PenumbraErrorEmit,
  PenumbraFile,
  PenumbraZipWriter,
  ProgressEmit,
  RemoteResource,
};

/** penumbra.supported.levels - Penumbra user agent support levels */
enum PenumbraSupportLevel {
  /** Old browser where Penumbra does not work at all */
  none = -0,
  /** Modern browser where Penumbra is not yet supported */
  possible = 0,
  /** Modern browser where file size limit is low */
  size_limited = 1,
  /** Modern browser with full support */
  full = 2,
}

const supported = (): PenumbraSupportLevel => -0;
supported.levels = PenumbraSupportLevel;

const MOCK_API = {
  get: () => Promise.resolve([]),
  save: () => Promise.resolve(undefined),
  supported,
  encrypt: () => Promise.resolve([]),
  decrypt: () => Promise.resolve([]),
  getDecryptionInfo: () =>
    Promise.resolve({
      key: 'test',
      iv: 'test',
      authTag: 'test',
    }),
  getBlob: () => Promise.resolve(new Blob()),
  getTextOrURI: () => [
    Promise.resolve({ data: 'test', type: 'text', mimetype: 'text/plain' }),
  ],
  zip: () => Promise.resolve(new ReadableStream()),
  setWorkerLocation: () => Promise.resolve(undefined),
};

// Mock penumbra in test environment
// eslint-disable-next-line no-restricted-globals
const p = SHOULD_MOCK_PENUMBRA ? MOCK_API : (self as any).penumbra;

// Ensure penumbra is defined
if (!p) {
  logger.warn(
    'Penumbra does not exist on self! Ensure it is loaded in the index.html file. You may need a more modern browser.',
  );
}

export const penumbra: PenumbraAPI = p || MOCK_API;

const penumbraWorkersSupported =
  penumbra &&
  penumbra.supported &&
  penumbra.supported() > penumbra.supported.levels.size_limited;

let scriptSrcAccessorPatched = false;
let workerLocationSet = false;

if (penumbraWorkersSupported) {
  if (!workerLocationSet) {
    penumbra.setWorkerLocation('/worker.penumbra.js');
    workerLocationSet = true;
  }
} else if (!scriptSrcAccessorPatched) {
  // Patch script.src setter to fix Penumbra chunk loading issue in Firefox ESR
  const penumbraChunkPath = /[\w\d-]+\.penumbra\.js$/;

  const scriptSrc = Object.getOwnPropertyDescriptor(
    HTMLScriptElement.prototype,
    'src',
  )!;
  Object.defineProperty(HTMLScriptElement.prototype, 'src', {
    ...scriptSrc,
    /**
     * Patched HTMLScriptElement.src setter
     *
     * @param args - Setter input
     * @returns void
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    set(...args: any[]) {
      const [input] = args;
      // eslint-disable-next-line no-restricted-globals
      const url = new URL(input, location.href);
      const matches = url.pathname.match(penumbraChunkPath);
      if (matches !== null) {
        // eslint-disable-next-line prefer-destructuring
        url.pathname = matches[0];
        // eslint-disable-next-line no-param-reassign
        args[0] = url.href;
      }
      // eslint-disable-next-line no-setter-return, @typescript-eslint/no-explicit-any
      return scriptSrc.set!.apply(this, args as [any]);
    },
  });
  scriptSrcAccessorPatched = true;
}
